import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Download, CheckCircle, Clock, XCircle,
  Bot, Building, Phone, DollarSign, Calendar,
  Hash, User, Mail
} from 'lucide-react';

export default function WithdrawalReceipt({ receipt, onClose }) {
  const receiptRef = useRef(null);

  const printReceipt = () => {
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Kudibot Withdrawal Receipt</title>
          <style>
            * { margin:0; padding:0; box-sizing:border-box; }
            body { font-family: 'Arial', sans-serif; background:#0a0a0a; color:white; padding:20px; }
            .receipt { max-width:500px; margin:0 auto; background:#111; border-radius:16px; padding:32px; border:1px solid #222; }
            .header { text-align:center; margin-bottom:24px; }
            .logo { font-size:24px; font-weight:900; color:#00ff88; margin-bottom:8px; }
            .title { font-size:18px; font-weight:700; margin-bottom:4px; }
            .subtitle { color:#666; font-size:12px; }
            .status { text-align:center; margin:20px 0; }
            .status-badge { display:inline-flex; align-items:center; gap:8px; padding:8px 20px; border-radius:50px; font-weight:700; font-size:14px; }
            .completed { background:rgba(0,255,136,0.1); color:#00ff88; border:1px solid rgba(0,255,136,0.3); }
            .pending { background:rgba(255,215,0,0.1); color:#ffd700; border:1px solid rgba(255,215,0,0.3); }
            .failed { background:rgba(255,68,68,0.1); color:#ff4444; border:1px solid rgba(255,68,68,0.3); }
            .amount { text-align:center; margin:20px 0; padding:20px; background:#0d0d0d; border-radius:12px; }
            .amount-usdt { font-size:36px; font-weight:900; color:#00ff88; }
            .amount-local { color:#666; font-size:14px; margin-top:4px; }
            .divider { border:none; border-top:1px solid #222; margin:20px 0; }
            .row { display:flex; justify-content:space-between; align-items:center; padding:10px 0; border-bottom:1px solid #1a1a1a; }
            .row:last-child { border-bottom:none; }
            .label { color:#666; font-size:12px; }
            .value { font-weight:600; font-size:13px; text-align:right; max-width:60%; }
            .footer { text-align:center; margin-top:24px; color:#444; font-size:11px; }
            @media print { body { background:white; color:black; } .receipt { background:white; border-color:#ddd; } }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="header">
              <div class="logo">⚡ Kudibot</div>
              <div class="title">Withdrawal Receipt</div>
              <div class="subtitle">Official Transaction Record</div>
            </div>
            <div class="status">
              <span class="status-badge ${receipt.status}">
                ${receipt.status === 'completed' ? '✓ Completed' : receipt.status === 'pending' ? '⏳ Pending' : '✗ Failed'}
              </span>
            </div>
            <div class="amount">
              <div class="amount-usdt">$${receipt.amount_usdt} USDT</div>
              <div class="amount-local">${receipt.currency} ${receipt.amount_local?.toLocaleString()}</div>
            </div>
            <hr class="divider"/>
            <div class="row"><span class="label">Transaction Ref</span><span class="value">${receipt.tx_ref || 'N/A'}</span></div>
            <div class="row"><span class="label">Method</span><span class="value">${receipt.method?.toUpperCase()}</span></div>
            ${receipt.method === 'bank' ? `
              <div class="row"><span class="label">Bank</span><span class="value">${receipt.bank_name}</span></div>
              <div class="row"><span class="label">Account Number</span><span class="value">${receipt.account_number}</span></div>
              <div class="row"><span class="label">Account Name</span><span class="value">${receipt.account_name}</span></div>
            ` : `
              <div class="row"><span class="label">Network</span><span class="value">${receipt.network}</span></div>
              <div class="row"><span class="label">Phone</span><span class="value">${receipt.phone_number}</span></div>
            `}
            <div class="row"><span class="label">Account Name</span><span class="value">${receipt.user_name}</span></div>
            <div class="row"><span class="label">Email</span><span class="value">${receipt.user_email}</span></div>
            <div class="row"><span class="label">Date</span><span class="value">${new Date(receipt.created_at).toLocaleString()}</span></div>
            ${receipt.completed_at ? `<div class="row"><span class="label">Completed</span><span class="value">${new Date(receipt.completed_at).toLocaleString()}</span></div>` : ''}
            <div class="footer">
              <p>Kudibot • kudibot.io</p>
              <p>This is an official receipt for your withdrawal transaction.</p>
            </div>
          </div>
        </body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  const statusConfig = {
    completed  : { color:'text-[#00ff88]', bg:'bg-[#00ff88]/10', border:'border-[#00ff88]/30', icon:<CheckCircle size={18}/>, label:'Completed'  },
    pending    : { color:'text-yellow-400', bg:'bg-yellow-500/10', border:'border-yellow-500/30', icon:<Clock size={18}/>,       label:'Pending'    },
    processing : { color:'text-[#00d4ff]', bg:'bg-[#00d4ff]/10', border:'border-[#00d4ff]/30', icon:<Clock size={18}/>,       label:'Processing' },
    failed     : { color:'text-red-400',   bg:'bg-red-500/10',   border:'border-red-500/30',   icon:<XCircle size={18}/>,     label:'Failed'     },
  };
  const sc = statusConfig[receipt.status] || statusConfig.pending;

  return (
    <AnimatePresence>
      <motion.div
        initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}>

        <motion.div
          initial={{opacity:0,scale:0.9,y:20}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:0.9,y:20}}
          onClick={e => e.stopPropagation()}
          className="w-full max-w-md glass rounded-3xl border border-[#1a1a1a] overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-[#1a1a1a]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00ff88] to-[#00d4ff] flex items-center justify-center">
                <Bot size={16} className="text-black"/>
              </div>
              <div>
                <p className="font-black text-sm">Kudibot</p>
                <p className="text-gray-500 text-xs">Withdrawal Receipt</p>
              </div>
            </div>
            <button onClick={onClose}
              className="p-2 rounded-xl glass border border-[#1a1a1a] text-gray-400 hover:text-white">
              <X size={16}/>
            </button>
          </div>

          <div ref={receiptRef} className="p-5">

            {/* Status */}
            <div className="text-center mb-5">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${sc.bg} ${sc.color} border ${sc.border} font-bold text-sm`}>
                {sc.icon} {sc.label}
              </div>
            </div>

            {/* Amount */}
            <div className="text-center p-5 bg-[#0d0d0d] rounded-2xl border border-[#1a1a1a] mb-5"
              style={{boxShadow:'0 0 30px rgba(0,255,136,0.05)'}}>
              <p className="text-gray-400 text-xs mb-1">Amount Withdrawn</p>
              <p className="text-4xl font-black text-[#00ff88]">${receipt.amount_usdt}</p>
              <p className="text-gray-500 text-sm mt-1">
                {receipt.currency} {receipt.amount_local?.toLocaleString()}
              </p>
            </div>

            {/* Details */}
            <div className="space-y-0 bg-[#0d0d0d] rounded-2xl border border-[#1a1a1a] overflow-hidden mb-5">
              {[
                { icon:<Hash size={14}/>,      label:'Transaction Ref', value: receipt.tx_ref || 'N/A',                              mono:true  },
                { icon:<DollarSign size={14}/>, label:'Method',          value: receipt.method?.toUpperCase(),                         mono:false },
                ...(receipt.method === 'bank' ? [
                  { icon:<Building size={14}/>, label:'Bank',            value: receipt.bank_name,                                     mono:false },
                  { icon:<Hash size={14}/>,     label:'Account Number',  value: receipt.account_number,                                mono:true  },
                  { icon:<User size={14}/>,     label:'Account Name',    value: receipt.account_name,                                  mono:false },
                ] : [
                  { icon:<Phone size={14}/>,    label:'Network',         value: receipt.network,                                       mono:false },
                  { icon:<Phone size={14}/>,    label:'Phone',           value: receipt.phone_number,                                  mono:true  },
                ]),
                { icon:<User size={14}/>,       label:'Account Holder',  value: receipt.user_name,                                     mono:false },
                { icon:<Mail size={14}/>,       label:'Email',           value: receipt.user_email,                                    mono:false },
                { icon:<Calendar size={14}/>,   label:'Date',            value: new Date(receipt.created_at).toLocaleString(),         mono:false },
                ...(receipt.completed_at ? [
                  { icon:<Calendar size={14}/>, label:'Completed',       value: new Date(receipt.completed_at).toLocaleString(),       mono:false },
                ] : []),
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a] last:border-0">
                  <div className="flex items-center gap-2 text-gray-500">
                    {row.icon}
                    <span className="text-xs">{row.label}</span>
                  </div>
                  <span className={`text-xs font-semibold text-right max-w-48 truncate ${row.mono ? 'font-mono text-[#00ff88]' : 'text-white'}`}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Footer Note */}
            <p className="text-center text-gray-600 text-xs">
              Official receipt from Kudibot • kudibot.io
            </p>

          </div>

          {/* Actions */}
          <div className="flex gap-3 p-5 border-t border-[#1a1a1a]">
            <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}}
              onClick={printReceipt}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#00ff88] to-[#00d4ff] text-black font-bold text-sm flex items-center justify-center gap-2">
              <Download size={16}/> Download / Print
            </motion.button>
            <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}}
              onClick={onClose}
              className="px-4 py-3 rounded-xl glass border border-[#1a1a1a] text-gray-400 hover:text-white text-sm font-semibold">
              Close
            </motion.button>
          </div>

        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
